import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { attemptAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { FaTrophy, FaPrint, FaDownload, FaChartBar, FaEdit, FaHome, FaChartLine } from 'react-icons/fa';

const ExamResults = () => {
  const { resultId } = useParams();
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (resultId) {
      fetchResults();
    }
  }, [resultId]);

  const fetchResults = async () => {
    try {
      setLoading(true);
      const response = await attemptAPI.getAttemptById(resultId);
      setResults(response.data?.data?.attempt);
    } catch (error) {
      console.error('Failed to fetch results:', error);
      setError(error);
      toast.error('Failed to load exam results');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100dvh',
        padding: 'env(safe-area-inset-top) 16px env(safe-area-inset-bottom) 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, var(--primary-50) 0%, var(--secondary-50) 100%)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2>Loading results...</h2>
          <p>Please wait while we fetch your exam results.</p>
        </div>
      </div>
    );
  }

  if (error || !results) {
    return (
      <div style={{
        minHeight: '100dvh',
        padding: 'env(safe-area-inset-top) 16px env(safe-area-inset-bottom) 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, var(--primary-50) 0%, var(--secondary-50) 100%)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2>No results found</h2>
          <p>Unable to load exam results. Please try again later.</p>
          <button className="btn btn-primary" onClick={() => navigate('/student')}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const formatTime = (seconds) => {
    if (!seconds) return '00:00:00';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getScoreColor = () => '#111827';

  const getScoreMessage = (percentage) => {
    if (percentage >= 90) return 'Excellent! Outstanding performance!';
    if (percentage >= 80) return 'Great job! Well done!';
    if (percentage >= 70) return 'Good work! Keep it up!';
    if (percentage >= 60) return 'Not bad! Room for improvement.';
    return 'Keep practicing! You can do better!';
  };

  // Extract data from API response
  const attempt = results || {};
  const certificate = attempt.certificate;
  const examScore = attempt.examScore; // New detailed score record
  
  const obtainedMarks = typeof attempt.obtainedMarks === 'number' ? attempt.obtainedMarks : (attempt.examScore?.obtainedMarks || 0);
  const totalMarks = attempt.totalMarks || attempt.exam?.totalMarks || attempt.examScore?.totalMarks || 0;
  const score = totalMarks > 0 ? Math.round((obtainedMarks / totalMarks) * 100) : (attempt.percentage || 0);
  const correctAnswers = obtainedMarks;
  const totalQuestions = attempt.exam?.totalQuestions || 0;
  const timeSpent = attempt.timeSpent || 0;

  // Extract detailed analytics from examScore
  const detailedAnalytics = examScore ? {
    wrongAnswers: examScore.wrongAnswers || 0,
    unanswered: examScore.unanswered || 0,
    totalTimeSpent: examScore.totalTimeSpent || 0,
    averageTimePerQuestion: examScore.averageTimePerQuestion || 0,
    timeEfficiency: examScore.timeEfficiency || 0,
    accuracy: examScore.accuracy || 0,
    speedScore: examScore.speedScore || 0,
    consistencyScore: examScore.consistencyScore || 0,
    difficultyScore: examScore.difficultyScore || 0,
    grade: examScore.grade || 'N/A',
    easyCorrect: examScore.easyCorrect || 0,
    easyTotal: examScore.easyTotal || 0,
    mediumCorrect: examScore.mediumCorrect || 0,
    mediumTotal: examScore.mediumTotal || 0,
    hardCorrect: examScore.hardCorrect || 0,
    hardTotal: examScore.hardTotal || 0
  } : null;

  return (
    <div className="min-h-screen bg-white py-8 px-4 relative" style={{ minHeight: '100dvh', paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      {/* Background Pattern */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{ display: 'none' }}
      />
      
      <div className="max-w-4xl mx-auto relative z-10 space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl p-6 shadow-xl text-center border border-gray-200 relative overflow-hidden">
          {/* Decorative Elements */}
          <div className="absolute -top-6 -right-6 w-12 h-12 bg-gradient-to-br from-purple-400/5 to-pink-400/5 rounded-full z-0" />
          <div className="absolute -bottom-4 -left-4 w-8 h-8 bg-gradient-to-br from-blue-400/5 to-purple-400/5 rounded-full z-0" />
          
          <div className="relative z-10">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Exam Results
            </h1>
            <h2 className="text-lg text-gray-700 mb-6">
              {attempt.exam?.title || 'Basic Mathematics Test'}
            </h2>

            {/* Score Display */}
            <div 
              className="inline-block p-8 rounded-full mb-6 relative"
              style={{
                background: '#fff',
                border: '2px solid #e5e7eb',
                boxShadow: '0 6px 16px rgba(17,24,39,0.06)'
              }}
            >
              <div 
                className="text-4xl font-extrabold mb-2"
                style={{
                  color: getScoreColor(score)
                }}
              >
                {obtainedMarks}/{totalMarks}
              </div>
              <div className="text-sm text-gray-500 font-semibold tracking-widest uppercase">
                Marks Obtained
              </div>
            </div>

            <p className="text-lg text-gray-700 mb-6 font-medium">
              {getScoreMessage(score)}
            </p>
          </div>

          {/* Certificate Display */}
          {certificate && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center shadow-md">
              <div className="text-4xl mb-3">
                <FaTrophy className="text-green-500" />
              </div>
              <h3 className="text-lg font-bold text-green-800 mb-2">
                🎉 Congratulations! You've earned a certificate!
              </h3>
              <p className="text-sm text-green-700 mb-4">
                Certificate #{certificate.certificateNumber}
              </p>
              <div className="flex gap-3 justify-center">
                <button 
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-semibold rounded-lg transition-all duration-200 hover:shadow-lg"
                  onClick={() => window.print()}
                >
                  <FaPrint />
                  Print
                </button>
                <button 
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold rounded-lg transition-all duration-200 hover:shadow-lg"
                  onClick={() => window.open(`/api/v1/certificates/${certificate.id}/download`, '_blank')}
                >
                  <FaDownload />
                  Download
                </button>
              </div>
            </div>
          )}

          {/* Key Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-xl border border-gray-200 text-center shadow-sm">
              <div className="text-2xl font-bold text-gray-800 mb-1">
                {obtainedMarks}/{totalMarks}
              </div>
              <div className="text-xs text-gray-600 font-medium uppercase tracking-wider">
                Marks Obtained
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-gray-200 text-center shadow-sm">
              <div className="text-2xl font-bold text-gray-800 mb-1">
                {formatTime(detailedAnalytics ? detailedAnalytics.totalTimeSpent : timeSpent)}
              </div>
              <div className="text-xs text-gray-600 font-medium uppercase tracking-wider">
                Time Spent
              </div>
            </div>
          </div>

          {/* Grade Card - Show separately if available */}
          {detailedAnalytics && detailedAnalytics.grade && (
            <div className="mt-4 flex justify-center">
              <div className="bg-white p-4 rounded-xl border border-gray-200 text-center shadow-sm min-w-[200px]">
                <div className="text-2xl font-bold text-purple-600 mb-1">
                  {detailedAnalytics.grade}
                </div>
                <div className="text-xs text-gray-600 font-medium uppercase tracking-wider">
                  Grade
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4 justify-center flex-wrap mt-6">
            <button 
              className="flex items-center gap-2 px-6 py-3 bg-gray-900 hover:bg-black text-white text-sm font-semibold rounded-lg transition-all duration-200 shadow-md"
              onClick={() => navigate('/student')}
            >
              <FaHome className="text-base" />
              Go to Dashboard
            </button>
          </div>
        </div>

        {/* Additional Details */}
        <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Additional Details
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
              <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                📋 Exam Information
              </h4>
              <div className="space-y-2 text-xs text-gray-700">
                <div className="flex justify-between">
                  <span className="font-medium">Started:</span> 
                  <span>{new Date(attempt.startedAt).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Completed:</span> 
                  <span>{new Date(attempt.completedAt).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Status:</span> 
                  <span className={`font-semibold ${
                    attempt.status === 'completed' ? 'text-green-600' : 'text-amber-600'
                  }`}>
                    {attempt.status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Duration:</span> 
                  <span>{attempt.exam?.duration || 60} min</span>
                </div>
              </div>
            </div>

            {detailedAnalytics && (detailedAnalytics.wrongAnswers > 0 || detailedAnalytics.unanswered > 0) && (
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                  📊 Performance
                </h4>
                <div className="space-y-2 text-xs text-gray-700">
                  {detailedAnalytics.wrongAnswers > 0 && (
                    <div className="flex justify-between">
                      <span className="font-medium">Wrong:</span> 
                      <span className="text-red-600">{detailedAnalytics.wrongAnswers}</span>
                    </div>
                  )}
                  {detailedAnalytics.unanswered > 0 && (
                    <div className="flex justify-between">
                      <span className="font-medium">Unanswered:</span> 
                      <span className="text-amber-600">{detailedAnalytics.unanswered}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="font-medium">Accuracy:</span> 
                    <span className="text-green-600 font-semibold">{Math.round(detailedAnalytics.accuracy)}%</span>
                  </div>
                </div>
              </div>
            )}

            {certificate && (
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                  🏆 Certificate
                </h4>
                <div className="space-y-2 text-xs text-gray-700">
                  <div className="flex justify-between">
                    <span className="font-medium">Number:</span> 
                    <span className="text-green-600">{certificate.certificateNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Issued:</span> 
                    <span>{new Date(certificate.issuedAt).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Expires:</span> 
                    <span>{new Date(certificate.expiresAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExamResults; 